import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Form, Button, Badge, Spinner, Modal, Alert } from "react-bootstrap";
import PageWrapperForm from "../components/PageWrapperForm";
import { ottService } from "../service/ottService";

const OTT_OPERATOR_ICONS = {
  "sony liv": "iconoir-tv",
  "sony live": "iconoir-tv",
  "zee 5": "iconoir-video-camera",
  "zee5": "iconoir-video-camera",
  "sun nxt": "iconoir-media-image",
  "eros now": "iconoir-play",
  "hungama play": "iconoir-play-cycle",
  "hungama music": "iconoir-music-note",
  shemaroo: "iconoir-film",
  "watcho ott pack": "iconoir-tv-fix",
  "watch ott pack": "iconoir-tv-fix",
};

const OttSubscription = () => {
  const [operators, setOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [form, setForm] = useState({
    mobile_no: "",
    customer_email: "",
    amount: "",
    pin: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [alertError, setAlertError] = useState(null);

  // Success Receipt Modal State
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const fetchOperators = useCallback(async () => {
    setLoadingOperators(true);
    try {
      const res = await ottService.getOperators();
      if (res.status && Array.isArray(res.data) && res.data.length > 0) {
        setOperators(res.data);
      } else {
        // Fallback default operators list if server returns empty
        setOperators([
          { name: "Sony Liv", operator_code: 17, code: "17" },
          { name: "Zee 5", operator_code: 18, code: "18" },
          { name: "Sun Nxt", operator_code: 19, code: "19" },
          { name: "Eros Now", operator_code: 20, code: "20" },
          { name: "Hungama Play", operator_code: 21, code: "21" },
          { name: "Hungama Music", operator_code: 22, code: "22" },
          { name: "Shemaroo", operator_code: 23, code: "23" },
          { name: "Watcho OTT Pack", operator_code: 168, code: "168" },
        ]);
      }
    } catch (err) {
      console.warn("Using default OTT operator list due to error:", err);
    } finally {
      setLoadingOperators(false);
    }
  }, []);

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  const handleSelectOperator = async (op) => {
    setSelectedOperator(op);
    setSelectedPlan(null);
    setForm((prev) => ({ ...prev, amount: "" }));
    setAlertError(null);
    setLoadingPlans(true);

    try {
      const res = await ottService.getPlans(op.operator_code);
      if (res.status && Array.isArray(res.data)) {
        setPlans(res.data);
      } else {
        setPlans([]);
      }
    } catch (err) {
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setForm((prev) => ({ ...prev, amount: plan.amount || "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlertError(null);

    if (!selectedOperator) {
      setAlertError("Please select an OTT Operator first.");
      return;
    }
    if (!form.mobile_no || form.mobile_no.length !== 10) {
      setAlertError("Please enter a valid 10-digit customer mobile number.");
      return;
    }
    if (!form.customer_email || !form.customer_email.includes("@")) {
      setAlertError("Please enter a valid customer email address.");
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setAlertError("Please select a plan or enter a valid amount.");
      return;
    }
    if (!form.pin) {
      setAlertError("Please enter your Transaction MPIN.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        mobile_no: form.mobile_no,
        customer_email: form.customer_email,
        operator_code: selectedOperator.operator_code,
        amount: form.amount,
        pin: form.pin,
        plan_id: selectedPlan?.plan_id || "",
      };

      const res = await ottService.createSubscription(payload);
      if (res && res.status) {
        setReceiptData({
          ...res,
          operator_name: selectedOperator.name,
          mobile_no: form.mobile_no,
          customer_email: form.customer_email,
        });
        setShowReceiptModal(true);

        // Reset form
        setForm({ mobile_no: "", customer_email: "", amount: "", pin: "" });
        setSelectedPlan(null);
      } else {
        setAlertError(res?.message || "Subscription activation failed.");
      }
    } catch (err) {
      setAlertError(err.message || "Transaction failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapperForm
      config={{
        title: "OTT Subscriptions",
        breadcrumbParent: "Service Manager",
      }}
    >
      <Row className="g-4">
        {/* Left Column: Operator Selector & Customer Form */}
        <Col lg={7} md={12}>
          <Card className="shadow-sm border-0 h-100" style={{ borderRadius: "16px" }}>
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-3 text-dark d-flex align-items-center">
                <i className="iconoir-tv me-2 text-primary fs-4"></i> 1. Select OTT Operator
              </h5>

              {loadingOperators ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="text-primary me-2" />
                  <span>Loading OTT platforms...</span>
                </div>
              ) : (
                <Row className="g-2 mb-4">
                  {operators.map((op) => {
                    const isSelected = selectedOperator?.operator_code === op.operator_code;
                    const iconClass =
                      OTT_OPERATOR_ICONS[(op.name || "").toLowerCase()] || "iconoir-media-image";

                    return (
                      <Col key={op.operator_code} xs={6} sm={4} md={3}>
                        <div
                          onClick={() => handleSelectOperator(op)}
                          className="p-3 text-center rounded-3 transition-all cursor-pointer border"
                          style={{
                            backgroundColor: isSelected ? "#eef2ff" : "#ffffff",
                            borderColor: isSelected ? "#4f46e5" : "#e5e7eb",
                            boxShadow: isSelected ? "0 4px 12px rgba(79,70,229,0.15)" : "none",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <div
                            className="mx-auto mb-2 d-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              width: "44px",
                              height: "44px",
                              backgroundColor: isSelected ? "#4f46e5" : "#f3f4f6",
                              color: isSelected ? "#ffffff" : "#4b5563",
                              fontSize: "20px",
                            }}
                          >
                            <i className={iconClass}></i>
                          </div>
                          <div
                            className="fw-bold"
                            style={{
                              color: isSelected ? "#4f46e5" : "#1f2937",
                              fontSize: "12px",
                              lineHeight: "1.25",
                              minHeight: "30px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            {op.name}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}

              <hr className="my-4 text-muted opacity-25" />

              <h5 className="fw-bold mb-3 text-dark d-flex align-items-center">
                <i className="iconoir-user me-2 text-primary fs-4"></i> 2. Customer Details & Authorization
              </h5>

              {alertError && (
                <Alert variant="danger" dismissible onClose={() => setAlertError(null)} className="py-2 small">
                  <i className="iconoir-warning-triangle me-2"></i>
                  {alertError}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Customer Mobile Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        maxLength="10"
                        placeholder="10-digit mobile number"
                        value={form.mobile_no}
                        onChange={(e) => setForm({ ...form, mobile_no: e.target.value.replace(/\D/g, "") })}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Customer Email *</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="customer@email.com"
                        value={form.customer_email}
                        onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Plan Amount (₹) *</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter or select amount"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Transaction MPIN *</Form.Label>
                      <Form.Control
                        type="password"
                        maxLength="6"
                        placeholder="Enter 4-digit PIN"
                        value={form.pin}
                        onChange={(e) => setForm({ ...form, pin: e.target.value })}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  disabled={submitting}
                  className="w-100 fw-bold py-3 text-white"
                  style={{
                    backgroundColor: "#4f46e5",
                    borderColor: "#4f46e5",
                    borderRadius: "12px",
                  }}
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Activating Subscription...
                    </>
                  ) : (
                    <>
                      <i className="iconoir-check-circle me-2"></i>
                      Activate Subscription {form.amount ? `(₹${form.amount})` : ""}
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column: Available Plans Grid */}
        <Col lg={5} md={12}>
          <Card className="shadow-sm border-0 h-100" style={{ borderRadius: "16px" }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold text-dark m-0 d-flex align-items-center">
                  <i className="iconoir-box me-2 text-primary fs-4"></i> Available Plans
                </h5>
                {selectedOperator && (
                  <Badge bg="primary-subtle" text="primary" className="border border-primary-subtle px-3 py-2">
                    {selectedOperator.name}
                  </Badge>
                )}
              </div>

              {!selectedOperator ? (
                <div className="text-center py-5 text-muted">
                  <i className="iconoir-tv fs-1 mb-2 d-block text-secondary"></i>
                  <p className="mb-0">Select an OTT Operator on the left to view available subscription plans.</p>
                </div>
              ) : loadingPlans ? (
                <div className="text-center py-5">
                  <Spinner animation="border" size="sm" className="text-primary me-2" />
                  <span>Fetching plans from operator...</span>
                </div>
              ) : plans.length > 0 ? (
                <div style={{ maxHeight: "450px", overflowY: "auto", paddingRight: "4px" }}>
                  <Row className="g-3">
                    {plans.map((p, idx) => {
                      const isSelected = selectedPlan?.plan_id === p.plan_id;
                      return (
                        <Col key={idx} xs={12}>
                          <div
                            onClick={() => handleSelectPlan(p)}
                            className="p-3 rounded-3 border transition-all cursor-pointer"
                            style={{
                              backgroundColor: isSelected ? "#f0fdf4" : "#ffffff",
                              borderColor: isSelected ? "#22c55e" : "#e5e7eb",
                              borderWidth: isSelected ? "2px" : "1px",
                              cursor: "pointer",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <h4 className="fw-bold m-0" style={{ color: "#1e1b4b" }}>
                                ₹{p.amount}
                              </h4>
                              {p.duration && (
                                <Badge bg="success" className="px-2 py-1">
                                  {p.duration} Days Validity
                                </Badge>
                              )}
                            </div>
                            <p className="text-muted small mb-0">{p.description || "Subscription Package"}</p>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="iconoir-info-circle fs-2 mb-2 d-block text-warning"></i>
                  <p className="mb-0">No pre-packaged plans found for this operator. You can enter the amount manually on the left.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Success / Receipt Modal */}
      <Modal show={showReceiptModal} onHide={() => setShowReceiptModal(false)} centered backdrop="static">
        <Modal.Header closeButton className="bg-success text-white py-3">
          <Modal.Title className="fs-6 text-white m-0 d-flex align-items-center">
            <i className="iconoir-check-circle me-2 fs-5"></i> Subscription Successful!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <div className="text-center mb-4">
            <div
              className="mx-auto mb-2 d-flex align-items-center justify-content-center bg-success text-white rounded-circle"
              style={{ width: "60px", height: "60px", fontSize: "30px" }}
            >
              <i className="iconoir-check"></i>
            </div>
            <h5 className="fw-bold text-dark mb-1">{receiptData?.message || "OTT Subscription Activated"}</h5>
            <span className="text-muted small">Ref: {receiptData?.reference_num || "-"}</span>
          </div>

          <div className="bg-light p-3 rounded-3 mb-3">
            <div className="d-flex justify-content-between py-1 border-bottom">
              <span className="text-muted small">Operator</span>
              <span className="fw-bold small">{receiptData?.operator_name || receiptData?.operator || "-"}</span>
            </div>
            <div className="d-flex justify-content-between py-1 border-bottom">
              <span className="text-muted small">Customer Mobile</span>
              <span className="fw-semibold small">{receiptData?.mobile_no || "-"}</span>
            </div>
            <div className="d-flex justify-content-between py-1 border-bottom">
              <span className="text-muted small">Customer Email</span>
              <span className="fw-semibold small">{receiptData?.customer_email || "-"}</span>
            </div>
            <div className="d-flex justify-content-between py-1 border-bottom">
              <span className="text-muted small">Amount Paid</span>
              <span className="fw-bold text-primary small">₹{receiptData?.amount}</span>
            </div>
            {receiptData?.commission_earned > 0 && (
              <div className="d-flex justify-content-between py-1 border-bottom">
                <span className="text-muted small">Commission Earned</span>
                <span className="fw-bold text-success small">₹{receiptData?.commission_earned}</span>
              </div>
            )}
            {receiptData?.wallet_balance !== null && receiptData?.wallet_balance !== undefined && (
              <div className="d-flex justify-content-between py-1">
                <span className="text-muted small">Updated Wallet Balance</span>
                <span className="fw-bold text-dark small">₹{receiptData?.wallet_balance}</span>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="success" size="sm" onClick={() => setShowReceiptModal(false)} className="w-100 fw-bold">
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </PageWrapperForm>
  );
};

export default OttSubscription;
